/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Clipboard } from "@capacitor/clipboard";
import { Capacitor } from "@capacitor/core";
import { IonButtons, IonContent, IonHeader, IonItem, IonLabel, IonMenuButton, IonPage, IonToolbar } from "@ionic/react";
import { useEffect, useState } from "react";
import { useHistory, withRouter } from "react-router";
import { useRecoilValueLoadable, useResetRecoilState, useSetRecoilState } from "recoil";
import { Button_Copy, Button_Menu } from "@assets/images";
import ContentContainer from "@components/common/ContentContainer";
import HeaderTitle from "@components/common/HeaderTitle";
import DashboardCard from "@components/home/DashboardCard";
import AuditProfileCard from "@components/profile/AuditProfileCard";
import PersonalStatementModal from "@components/profile/PersonalStatementModal";
import SummaryOfWorkModal from "@components/profile/SummaryOfWorkModal";
import AuthenticationController from "@controllers/AuthenticationController";
import DataController from "@controllers/DataController";
import * as FirebaseService from "@controllers/FirebaseService";
import {
  IAccessibilitySettings,
  IEvidence,
  IEvidenceDraft,
  IParapassQuizData,
  IParapassUsageData,
  IParapassBooksProgress,
  IPlusUsageData,
  IUser,
} from "@interfaces/Interfaces";
import {
  accessibilityAtom,
  debugUserAtom,
  draftsHintDismissedAtom,
  evidenceAtom,
  evidenceDraftsAtom,
  guidelinesCardDismissedAtom,
  parapassQuizDataAtom,
  parapassUsageDataAtom,
  parapassBooksProgressAtom,
  plusDataAtom,
  programDataAtom,
  programDataVersionInfoAtom,
  programInfoDismissedAtom,
  progressCheckDataAtom,
  userAtom,
  userProgramsAtom,
  welcomeDismissedAtom,
} from "@state/State";
import { loadableHasValue } from "@utils/recoilUtils";
import "./Profile.css";
import AuditProfileHelpModal from "@components/profile/AuditProfileHelpModal";
import { updateOnboardingAtom } from "@state/UpdateState";
import { evidenceFiltersAtom } from "@state/FilterState";
import { previousIncidentDetailsAtom } from "@state/EvidenceState";
import { aiAssistDisclaimerAcceptedAtom } from "@state/AIAssistState";
import { useQueryClient } from "@tanstack/react-query";

const Profile: React.FC = () => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const user = useRecoilValueLoadable<IUser | null>(userAtom);

  const setEvidence = useSetRecoilState<IEvidence[] | null>(evidenceAtom);
  const setGuidelinesCardDismissed = useSetRecoilState<boolean>(guidelinesCardDismissedAtom);
  const setWelcomeDismissed = useSetRecoilState<boolean>(welcomeDismissedAtom);
  const setPlusData = useSetRecoilState<IPlusUsageData[] | null>(plusDataAtom);
  const setParapassQuizData = useSetRecoilState<IParapassQuizData[] | null>(parapassQuizDataAtom);
  const setParapassUsageData = useSetRecoilState<IParapassUsageData[] | null>(parapassUsageDataAtom);
  const setParapassBooksProgress = useSetRecoilState<IParapassBooksProgress[] | null>(parapassBooksProgressAtom);
  const setAccessibilitySettings = useSetRecoilState<IAccessibilitySettings | null>(accessibilityAtom);
  const setInfoDismissed = useSetRecoilState<any[]>(programInfoDismissedAtom);
  const setDraftsHintDismissed = useSetRecoilState<boolean>(draftsHintDismissedAtom);
  const setDrafts = useSetRecoilState<IEvidenceDraft[] | null>(evidenceDraftsAtom);
  const setEvidenceFilters = useSetRecoilState(evidenceFiltersAtom);
  const resetUser = useResetRecoilState(userAtom);
  const resetAllProgressCheckData = useResetRecoilState(progressCheckDataAtom);
  const resetUserPrograms = useResetRecoilState(userProgramsAtom);
  const resetPreviousIncidentDetails = useResetRecoilState(previousIncidentDetailsAtom);
  const resetAiAssistDisclaimerAccepted = useResetRecoilState(aiAssistDisclaimerAcceptedAtom);

  const debugUserLoadable = useRecoilValueLoadable(debugUserAtom);
  const setDebugUser = useSetRecoilState(debugUserAtom);
  const programDataLoadable = useRecoilValueLoadable(programDataAtom);
  const setProgramData = useSetRecoilState(programDataAtom);
  const programVersionInfoLoadable = useRecoilValueLoadable(programDataVersionInfoAtom);
  const setProgramVersionInfo = useSetRecoilState(programDataVersionInfoAtom);
  const updateOnboardingLoadable = useRecoilValueLoadable(updateOnboardingAtom);
  const setUpdateOnboarding = useSetRecoilState(updateOnboardingAtom);

  const [summaryOfWorkModalVisible, setSummaryOfWorkModalVisible] = useState<boolean>(false);
  const [personalStatementModalVisible, setPersonalStatementModalVisible] = useState<boolean>(false);

  const [helpModalVisible, setHelpModalVisible] = useState<boolean>(false);

  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    const setPageName = async (): Promise<void> => {
      await FirebaseService.setScreenName("profile");
    };

    setPageName();
  }, []);

  const logout = async (): Promise<void> => {
    await AuthenticationController.logout();
    setEvidence(null);
    setPlusData(null);
    setParapassQuizData(null);
    setParapassUsageData(null);
    setParapassBooksProgress(null);
    setAccessibilitySettings(null);
    setWelcomeDismissed(false);
    setDraftsHintDismissed(false);
    setGuidelinesCardDismissed(false);
    setInfoDismissed([]);
    setDrafts(null);
    resetUser();
    resetAllProgressCheckData();
    resetUserPrograms();
    resetPreviousIncidentDetails();
    resetAiAssistDisclaimerAccepted();
    loadableHasValue(debugUserLoadable) && setDebugUser(false);
    loadableHasValue(programDataLoadable) && setProgramData([]);
    loadableHasValue(programVersionInfoLoadable) && setProgramVersionInfo([]);
    loadableHasValue(updateOnboardingLoadable) && setUpdateOnboarding(null);
    setEvidenceFilters([]);

    queryClient.removeQueries({ queryKey: ["app-versions"] });

    history.replace("/onboarding/login");
  };

  const navigateToPage = (page: string): void => {
    history.push(page);
  };

  const copyHCPCButton = async (): Promise<void> => {
    setCopied(true);

    if (await DataController.isWebAsync()) {
      navigator.clipboard.writeText(user.contents?.HCPCNumber);
    } else {
      Clipboard.write({
        string: user.contents?.HCPCNumber,
      });
    }

    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  const toggleSummaryOfWorkModalVisible = (value: boolean): void => {
    setSummaryOfWorkModalVisible(value);
  };

  const togglePersonalStatementModalVisible = (value: boolean): void => {
    setPersonalStatementModalVisible(value);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonMenuButton>
              <img
                alt="Menu"
                title="Menu"
                src={Button_Menu}
                className="menuIcon"
              />
            </IonMenuButton>
          </IonButtons>
          <HeaderTitle>{"Profile"}</HeaderTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <AuditProfileHelpModal
          visible={helpModalVisible}
          closeModal={() => setHelpModalVisible(false)}
        />
        <SummaryOfWorkModal
          visible={summaryOfWorkModalVisible}
          closeModal={(value) => toggleSummaryOfWorkModalVisible(value)}
        />
        <PersonalStatementModal
          visible={personalStatementModalVisible}
          closeModal={(value) => togglePersonalStatementModalVisible(value)}
        />
        <ContentContainer id="profileContentContainer">
          <DashboardCard
            title={user ? `${user.state === "hasValue" ? user.contents?.name || user.contents?.email : ""}` : "User"}
            subtitle="Your details"
            isProfile
            sideButton
            sideButtonText="Edit"
            buttonClicked={() => navigateToPage("/profile/edit")}
            className="pt-5"
          >
            <div style={{ marginTop: -22 }}>
              <div className="homeCardUserInfoVertical">
                <div className="homeCardUserDetailsHeader">{"Email"}</div>
                <div className="homeCardUserDetailsText">{user.contents?.email || "Unknown"}</div>
              </div>
              {user.state === "hasValue" && user.contents?.HCPCNumber && (
                <div className="homeCardUserInfoVertical">
                  <div className="homeCardUserDetailsHeader">{"Registration number"}</div>
                  <div className="homeCardHCPCRegistrationNumberContainer">
                    <div className="homeCardHCPCRegistrationNumber">{user.contents?.HCPCNumber || "Unknown"}</div>
                    <button
                      className="homeCardUserCopyButton"
                      disabled={copied}
                      onClick={copyHCPCButton}
                    >
                      {copied ? (
                        <div className="homeCardUserCopyButtonText">{"Copied"}</div>
                      ) : (
                        <>
                          <div className="homeCardUserCopyButtonText">{"Copy"}</div>
                          <img
                            src={Button_Copy}
                            className="hcpcCopyIcon"
                            alt=""
                          />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div
              className="mt-3 border-t"
              style={{ marginBottom: "-16px", marginLeft: "-14px", marginRight: "-14px" }}
            >
              <IonItem
                button={true}
                onClick={logout}
                className="profileSettingsButton"
                detail={false}
                lines="none"
              >
                <div className="profileSettingsButtonContainer !min-h-12">
                  <div className="profileSettingsButtonInnerContainer ml-4">
                    <IonLabel className={`profileSettingButtonTitle ${"profileSettingLogout"}`}>Sign out</IonLabel>
                  </div>
                </div>
              </IonItem>
            </div>
          </DashboardCard>
          <AuditProfileCard helpButtonClicked={() => setHelpModalVisible(true)} />
        </ContentContainer>
      </IonContent>
    </IonPage>
  );
};

export default withRouter(Profile);
